/**
 * OWA-1518: the server refuses a save whose invoice number another invoice of the firm already
 * holds (409). A draft is renumbered and saved again, so the user is not left with a draft the
 * server will not store; an invoice that is no longer a draft keeps its number and the refusal is
 * shown as for any refused save.
 */
import store from '../store/store';

/** The server's text (outlaw_practice_dao DaoError::InvoiceNumberTaken). */
export const INVOICE_NUMBER_TAKEN = 'Another invoice already uses this invoice number. Choose a different number.';

/** Is this refusal the server saying the invoice number is taken? */
export function isInvoiceNumberRefusal(entityName: string, status: number, message?: string): boolean {
    return entityName === 'invoice' && Number(status) === 409 && message === INVOICE_NUMBER_TAKEN;
}

/**
 * The number after the refused one and after every number this tab knows, padded to six digits as
 * FinancialHelper.findNextInvoiceNumber does. Numbers that are not numeric are ignored. Each
 * refusal moves the draft to a higher number, so the retries end.
 */
export function nextNumberAfterRefusal(refused: string, known: string[]): string {
    const numbers = [refused, ...known].map(number => Number.parseInt(number)).filter(number => !Number.isNaN(number));
    const max = numbers.length > 0 ? Math.max(...numbers) : 0;
    return `${max + 1}`.padStart(6, '0');
}

/**
 * A draft that was refused for its number, with the next free number; null for anything else.
 * The answer can arrive after the user changed the invoice, so the retry is made only while the
 * refused version is still the one this tab shows and it is still a draft: a newer edit stays
 * queued as it is and is renumbered, if refused, by its own answer.
 */
export function renumberedDraft<T extends {invoiceId?: string, id?: string, addedOn?: number, invoiceStatus?: string | null, invoiceNumber?: string, clone?: () => T}>(
    sent: T | undefined, entityName: string, status: number, message?: string
): T | null {
    if (!sent || !isInvoiceNumberRefusal(entityName, status, message) || sent.invoiceStatus !== 'D') return null;
    // The store's ArrayMap (find and map are the methods it shares with Array)
    type Shown = {invoiceId?: string, id?: string, addedOn?: number, invoiceStatus?: string | null, invoiceNumber: string};
    const invoices = store.getState().invoices as unknown as {find: (fn: (invoice: Shown) => boolean) => Shown | null | undefined, map: (fn: (invoice: Shown) => string) => string[]};
    const id = sent.invoiceId ?? sent.id;
    const shown = invoices.find(invoice => (invoice.invoiceId ?? invoice.id) === id);
    if (!shown || shown.invoiceStatus !== 'D' || Number(shown.addedOn) > Number(sent.addedOn)) return null;
    const known = invoices.map(invoice => invoice.invoiceNumber);
    const renumbered = typeof sent.clone === 'function' ? sent.clone() : {...sent} as T;
    renumbered.invoiceNumber = nextNumberAfterRefusal(sent.invoiceNumber ?? '', known);
    return renumbered;
}
